import {computed, type Ref} from "vue";
import type {
  BIInteractionData,
  BIClickEmitMappingData,
  BIDrillDownTarget,
  BIDrillDownByDimension,
  BIDrillDownByData,
  BIDrillDownFilterMetadata,
  BIDrillDownPointValues,
  BILinkTarget,
  BILinkPointValues,
} from "@/components/bi/biChartTypes.ts";

/**
 * Extracts the `interactionData` payload attached to non-chart widgets (Table, EnhancedTable,
 * MetricsCards) into typed computed refs for each interaction kind. Shared by
 * `useBIInteractionsForAgGrid` and `MetricsCardDashboardWidget` so every consumer sees the
 * same empty-array defaults and the same optional-chaining behavior.
 */
export function useBIInteractionData(chartLine: Ref<{interactionData?: BIInteractionData} | undefined>) {
  const interactionData = computed<BIInteractionData | undefined>(
    () => chartLine.value?.interactionData,
  );
  const clickEmitData = computed<BIClickEmitMappingData[]>(() => interactionData.value?.clickEmitData ?? []);
  const drillTargets = computed<BIDrillDownTarget[]>(() => interactionData.value?.drillDownData?.targets ?? []);
  const drillFilterMetadata = computed<Record<string, BIDrillDownFilterMetadata>>(
    () => interactionData.value?.drillDownData?.filterMetadata ?? {},
  );
  const drillPoints = computed<BIDrillDownPointValues[]>(() => interactionData.value?.drillDownData?.points ?? []);
  const linkTargets = computed<BILinkTarget[]>(() => interactionData.value?.linkData?.links ?? []);
  const linkPoints = computed<BILinkPointValues[]>(() => interactionData.value?.linkData?.points ?? []);
  const drillByDimensions = computed<BIDrillDownByDimension[]>(
    () => interactionData.value?.drillDownByData?.dimensions ?? [],
  );
  const drillByData = computed<BIDrillDownByData | undefined>(() => interactionData.value?.drillDownByData);

  return {
    interactionData,
    clickEmitData,
    drillTargets,
    drillFilterMetadata,
    drillPoints,
    linkTargets,
    linkPoints,
    drillByDimensions,
    drillByData,
  };
}
